import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { EUR, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { DonorTypeBadge } from "@/components/StatusBadge";
import NewPaymentDialog, { ReceiptButton, EditPaymentButton, DeletePaymentButton } from "@/components/NewPaymentDialog";
import { useAuth, isAdminOrSegreteria } from "@/lib/auth";

const PAGE_SIZE = 50;

export default function Donations() {
  const { user } = useAuth();
  const canManage = isAdminOrSegreteria(user);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState("all");
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => { setPage(0); }, [q, method]);

  const { data: result } = useQuery({
    queryKey: ["payments-page", { q, method, page }],
    queryFn: async () => {
      const params = { paginated: true, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (q) params.search = q;
      if (method !== "all") params.method = method;
      return (await api.get("/payments", { params })).data;
    },
    keepPreviousData: true,
  });

  const payments = result?.items || [];
  const total = result?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // KPI: totale filtrato calcolato lato server tramite report/summary
  const { data: kpi } = useQuery({
    queryKey: ["payments-kpi", { q, method }],
    queryFn: async () => {
      const params = {};
      if (method !== "all") params.method = method;
      // il KPI non filtra per search (limitazione temporanea)
      return (await api.get("/reports/summary", { params })).data;
    },
    enabled: !q,
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Donazioni"
        subtitle="Elenco completo dei pagamenti registrati."
        actions={
          <Button
            data-testid="donations-add-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
          >
            <Plus size={14} className="mr-1.5"/> Nuova donazione
          </Button>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            {q ? "Pagina corrente" : "Totale"}
          </div>
          <div className="text-2xl font-semibold mt-1" data-testid="donations-total">
            {EUR.format(q ? payments.reduce((a, b) => a + b.amount, 0) : (kpi?.total || 0))}
          </div>
          <div className="text-[11px] text-stone-500">
            {q ? `${payments.length} in pagina` : `${total} donazioni totali`}
          </div>
        </Card>
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              placeholder="Cerca donatore (nome, CF, email)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="donations-search"
            />
          </div>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-40" data-testid="donations-method-filter"><SelectValue placeholder="Metodo"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i metodi</SelectItem>
              <SelectItem value="bonifico">Bonifico</SelectItem>
              <SelectItem value="contanti">Contanti</SelectItem>
              <SelectItem value="stripe">Stripe</SelectItem>
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="altro">Altro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
                <TableHead className="uppercase text-[10px] tracking-widest">Data</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Donatore</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Tipo</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest text-right">Importo</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Metodo</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Riferimento</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-stone-400">Nessuna donazione.</TableCell></TableRow>}
              {payments.map((p, i) => (
                <TableRow key={p.id} className="animate-in-row" style={{ animationDelay: `${i * 6}ms` }}>
                  <TableCell className="text-sm">{fmtDate(p.payment_date)}</TableCell>
                  <TableCell>
                    <Link to={`/donatori/${p.donor_id}`} className="font-medium hover:underline">{p.donor_name}</Link>
                    {p.donor_org && (p.donor_city || p.donor_referente) && (
                      <div className="text-[11px] text-stone-500 mt-0.5" data-testid={`donation-donor-ref-${p.id}`}>
                        {p.donor_city || p.donor_referente}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{p.donor_type && <DonorTypeBadge type={p.donor_type}/>}</TableCell>
                  <TableCell className="text-right font-semibold">{EUR.format(p.amount)}</TableCell>
                  <TableCell className="text-sm capitalize">{p.method}</TableCell>
                  <TableCell className="text-sm text-stone-500 mono">{p.receipt_number || p.reference || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <ReceiptButton payment={p}/>
                      {canManage && <EditPaymentButton payment={p}/>}
                      {canManage && <DeletePaymentButton payment={p}/>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-stone-600" data-testid="donations-pagination">
          <div>Pagina <b>{page + 1}</b> di <b>{totalPages}</b> · {total} donazioni totali</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="donations-page-prev">← Precedente</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} data-testid="donations-page-next">Successiva →</Button>
          </div>
        </div>
      )}
      <NewPaymentDialog open={createOpen} onOpenChange={setCreateOpen}/>
    </div>
  );
}
